import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  autoSignIn,
  confirmSignIn,
  signIn,
  signOut,
  signUp,
} from 'aws-amplify/auth';
import {
  Form,
  Button,
  TextFieldControlled as Input,
  ToggleFieldControlled as Toggle,
  SelectFieldMinimal,
  FieldWrap,
} from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useEventsTracker } from 'contexts/eventsTracker';
import { StatesListItem, useStatesList } from 'hooks/useStatesList';
import { useMedicalCardParams } from 'hooks/useMedicalCardParams';
import { useMedicalCardSignup } from 'hooks/useMedicalCardSignup';
import { setStateCookie } from 'lib/cookies/state-cookie';
import { configureAmplify } from 'lib/amplify';
import { persistLoginSession } from 'lib/loginSession';
import { clientId } from 'lib/tracking/gtag';
import { useRouter } from 'next/router';

const MEMBERSHIP_ONLY_STATE_CODE = 'fl';
const RESEND_COOLDOWN_SECONDS = 60;

declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    before?: string;
  }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * The patient app lives on a different origin, so `path` must be relative.
 * It is stored alongside the Cognito challenge session and used as the
 * post-verification destination once the user clicks the magic link.
 */
function toRelativePath(absoluteOrRelative: string): string {
  try {
    return new URL(absoluteOrRelative).pathname;
  } catch {
    return absoluteOrRelative.startsWith('/')
      ? absoluteOrRelative
      : `/${absoluteOrRelative}`;
  }
}

type ViewState =
  | { kind: 'form' }
  | { kind: 'sent'; email: string; path: string }
  | { kind: 'error'; email: string; path: string };

type SignupFormPasswordlessProps = {
  className?: string;
  formSubmitText?: string;
  stateCode: string;
  promoCode?: string | null;
  showBorder?: boolean;
  ctaLocation?: string;
  showStateSelect?: boolean;
  redirectOnStateChange?: boolean;
  onStateChange?: (stateCode?: string) => void;
  priceTag?: React.ReactNode;
  partnerData?: React.ReactNode;
};

const SignupFormPasswordless: React.FC<SignupFormPasswordlessProps> = memo(
  ({
    stateCode: initialStateCode,
    className,
    formSubmitText,
    showBorder = true,
    ctaLocation,
    showStateSelect = true,
    redirectOnStateChange = true,
    onStateChange,
    promoCode = null,
    priceTag,
    partnerData,
  }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [view, setView] = useState<ViewState>({ kind: 'form' });
    const [cooldown, setCooldown] = useState<number>(0);
    const [isSending, setIsSending] = useState<boolean>(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const statesList = useStatesList(false);
    const { trackEvent } = useEventsTracker();
    const { translations, cardPage, statePages } = useWordPressContext();
    const { createSignup, updateSignup } = useMedicalCardSignup();

    const baseUrl = process.env.NEXT_PUBLIC_PRODUCT_ENDPOINT;
    const medicalCardParams = useMedicalCardParams();
    const [discountCode, setDiscountCode] = useState<string>(promoCode ?? null);
    const { query } = useRouter();
    const posthog = usePostHog();
    const [fpromTid, setFpromTid] = useState<string | null>(null);
    const [fpromRefId, setFpromRefId] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string>('');
    const [distinctId, setDistinctId] = useState<string>('');
    const [stateCode, setStateCode] = useState<string>(initialStateCode);
    const [stateError, setStateError] = useState<boolean>(false);

    useEffect(() => {
      setStateCode(initialStateCode);
    }, [initialStateCode]);

    useEffect(() => {
      setFpromTid(getCookie('_fprom_tid'));
      setFpromRefId(getCookie('_fprom_ref'));
    }, []);

    useEffect(() => {
      setSessionId(posthog.get_session_id() ?? '');
      setDistinctId(posthog.get_distinct_id() ?? '');
    }, [posthog]);

    useEffect(() => {
      if (query.discount_code && query.discount_code != '') {
        setDiscountCode(query.discount_code.toString().toUpperCase());
      }
    }, [query]);

    useEffect(() => {
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }, []);

    const selectedStateObject: StatesListItem = useMemo(
      () => statesList.find(item => item.value === stateCode),
      [statesList, stateCode],
    );

    const handleSelectState = (selectedStateCode?: string) => {
      const hasStateCode = selectedStateCode && selectedStateCode !== '';

      if (hasStateCode) {
        setStateCookie(selectedStateCode);
      }

      // e.g. homepage: keep the user on the page and use the selected state
      if (!redirectOnStateChange) {
        const nextStateCode = hasStateCode ? selectedStateCode : undefined;
        setStateCode(nextStateCode);
        setStateError(false);
        onStateChange?.(nextStateCode);
        return;
      }

      const url = hasStateCode
        ? statePages.find(({ code }) => code === selectedStateCode)?.uri
        : cardPage?.uri;

      if (url) {
        window.location.assign(url);
      }
    };

    const startCooldown = useCallback(() => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            clearInterval(timerRef.current!);
            timerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, []);

    const formatTime = (seconds: number) => {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    /**
     * Same destination + params the component used to redirect to, expressed
     * as a relative path so it can be replayed by the patient app.
     */
    const buildDestinationPath = useCallback(
      (email: string, uuid: string | null): string => {
        const isMembershipState =
          stateCode?.toLowerCase() === MEMBERSHIP_ONLY_STATE_CODE;
        const targetBaseUrl = isMembershipState
          ? `${process.env.NEXT_PUBLIC_LOGIN_URL}/membership`
          : baseUrl;

        const params = new URLSearchParams();

        if (medicalCardParams && typeof medicalCardParams === 'object') {
          Object.entries(medicalCardParams)
            .filter(([, value]) => value != null && value !== '')
            .forEach(([key, value]) => params.set(key, String(value)));
        }

        params.set('session_id', sessionId);
        params.set('distinct_id', distinctId);
        params.set('state_code', stateCode?.toLowerCase() ?? '');
        params.set('email', email);
        if (discountCode) params.set('discount_code', discountCode);
        if (fpromRefId) params.set('fp_ref', fpromRefId);
        if (fpromTid) params.set('fp_tid', fpromTid);
        if (uuid) params.set('uuid', uuid);

        return `${toRelativePath(targetBaseUrl)}?${params.toString()}`;
      },
      [
        baseUrl,
        stateCode,
        medicalCardParams,
        sessionId,
        distinctId,
        discountCode,
        fpromRefId,
        fpromTid,
      ],
    );

    const createPatientRegistry = useCallback(
      async (email: string): Promise<string | null> => {
        try {
          const createRes = await createSignup({
            ...medicalCardParams,
            email,
            state_code: stateCode?.toLowerCase(),
            ...(discountCode ? { discount_code: discountCode } : {}),
          });

          if (createRes.error) return null;

          const updateRes = await updateSignup({
            email,
            state_code: stateCode?.toLowerCase(),
            uuid: createRes.data.uuid,
            accepted_terms_of_service: true,
          });

          if (updateRes.error || updateRes.errors) return null;

          return createRes.data.uuid;
        } catch (error) {
          console.error('createPatientRegistry failed:', error);
          return null;
        }
      },
      [stateCode, medicalCardParams, discountCode, createSignup, updateSignup],
    );

    const startSignIn = (email: string) =>
      signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP',
        },
      });

    const initiateSignIn = useCallback(
      async (email: string, path: string) => {
        configureAmplify();
        setIsSending(true);

        try {
          try {
            await signOut();
          } catch {
            // no session to clear
          }

          let result;

          try {
            result = await startSignIn(email);
          } catch (error) {
            if (error?.name !== 'UserNotFoundException') throw error;

            const signUpResult = await signUp({
              username: email,
              options: {
                autoSignIn: { authFlowType: 'USER_AUTH' },
                userAttributes: {
                  given_name: '',
                  family_name: '',
                  phone_number: '',
                  country_code: '',
                  'custom:createdAt': new Date().toISOString(),
                },
              },
            });

            if (signUpResult.nextStep?.signUpStep !== 'COMPLETE_AUTO_SIGN_IN') {
              throw new Error(
                `Unexpected signUp step: ${signUpResult.nextStep?.signUpStep}`,
              );
            }
            trackEvent({
              event: 'user_account_created',
              is_in_app_browser: 'false',
              is_test: 'false',
              page_path: 'medical-card',
              service: 'medicalcard',
            });

            result = await autoSignIn();

            if (
              result.nextStep?.signInStep ===
              'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION'
            ) {
              result = await confirmSignIn({ challengeResponse: 'EMAIL_OTP' });
              console.log(
                '[cognito] after challenge selection:',
                result.nextStep,
              );
            }
          }

          if (
            result.nextStep?.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
          ) {
            console.warn(
              'Unexpected signIn step:',
              result.nextStep?.signInStep,
            );
          }

          await persistLoginSession(email, path);

          setView({ kind: 'sent', email, path });
          startCooldown();
        } catch (error) {
          console.error('Cognito signIn error:', error);
          setView({ kind: 'error', email, path });
        } finally {
          setIsSending(false);
        }
      },
      [startCooldown],
    );

    const onSubmit = async ({ email, accepted_terms_of_service }) => {
      if (!email || !accepted_terms_of_service) {
        setIsLoading(false);
        console.error('Please fill in all fields');
        return;
      }

      if (!stateCode) {
        setStateError(true);
        return;
      }

      setIsLoading(true);

      const trimmedEmail = email.trim();
      const uuid = await createPatientRegistry(trimmedEmail);

      const referrerData = medicalCardParams?.utm_referrer
        ? { page_referrer: medicalCardParams.utm_referrer }
        : {};
      const discountCodeData = discountCode
        ? { discount_code: discountCode }
        : {};
      const ctaLocationData = ctaLocation ? { cta_location: ctaLocation } : {};

      trackEvent({
        event: 'generate_lead',
        service: 'medical_card',
        location: translations[stateCode.toUpperCase()],
        client_id: clientId(),
        ...referrerData,
        ...discountCodeData,
        ...ctaLocationData,
      });

      const path = buildDestinationPath(trimmedEmail, uuid);
      await initiateSignIn(trimmedEmail, path);

      setIsLoading(false);
    };

    const handleResend = async () => {
      if (cooldown > 0 || isSending) return;
      if (view.kind !== 'sent') return;
      await initiateSignIn(view.email, view.path);
    };

    const handleRetry = async () => {
      if (view.kind !== 'error') return;
      await initiateSignIn(view.email, view.path);
    };

    const resetToForm = () => setView({ kind: 'form' });

    const wrapperClassName = `grid transition-opacity duration-500 gap-4 p-4 ${
      showBorder ? 'bg-[#F6FEF9] border border-green-500' : ''
    } rounded-lg w-fit max-w-md ${className}`;

    if (view.kind === 'error') {
      return (
        <div className={wrapperClassName}>
          <div className="grid gap-y-4">
            <p className="font-medium">We couldn&apos;t send your link</p>
            <p className="text-sm">
              The sign-in link to{' '}
              <strong className="break-all">{view.email}</strong> didn&apos;t go
              through. Try again, or start over with a different email.
            </p>
            <div className="grid gap-y-2">
              <Button
                label="Try again"
                variant="primary"
                className="w-full rounded-lg"
                loading={isSending}
                onClick={handleRetry}
              />
              <button
                type="button"
                onClick={resetToForm}
                className="text-sm font-medium underline"
              >
                Start over
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (view.kind === 'sent') {
      return (
        <div className={wrapperClassName}>
          <div className="grid gap-y-4">
            <p className="font-medium">Check your email</p>
            <p className="text-sm">
              We sent a sign-in link to{' '}
              <strong className="break-all">{view.email}</strong>.<br /> Open it
              to continue your application.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0 || isSending}
                className={[
                  'text-base font-medium underline',
                  cooldown === 0 && !isSending
                    ? 'text-yellow-600'
                    : 'cursor-not-allowed text-yellow-600',
                ].join(' ')}
              >
                {isSending ? 'Sending…' : 'Send it again'}
              </button>
              {cooldown > 0 && (
                <span className="text-sm tabular-nums text-yellow-700">
                  {formatTime(cooldown)}
                </span>
              )}
            </div>
            <div className="grid gap-y-1 text-sm text-gray-700">
              <p>Can&apos;t find it? Check your spam folder.</p>
              <p>
                Wrong email?{' '}
                <button
                  type="button"
                  onClick={resetToForm}
                  className="font-medium underline"
                >
                  Start over
                </button>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={wrapperClassName}>
        {partnerData ? <div className="-mx-4 -mt-4">{partnerData}</div> : null}
        {priceTag ? <div className="-mx-4 -mt-4">{priceTag}</div> : null}
        <Form
          className="md:gap-x-3 gap-y-4"
          mode="onSubmit"
          onSubmit={onSubmit}
          defaultValues={{
            stateCode,
            email: '',
            accepted_terms_of_service: false,
          }}
        >
          {showStateSelect ? (
            <div className="grid gap-y-2 js-form-field relative">
              <p className="font-medium">Select your state</p>
              <FieldWrap name="stateCode">
                <SelectFieldMinimal
                  aria-label="Select your state"
                  defaultValue={selectedStateObject}
                  placeholder={translations['Select State']}
                  options={statesList}
                  onSelect={target => handleSelectState(target.value)}
                />
              </FieldWrap>
              {stateError ? (
                <p className="text-sm text-red-600">Please select your state</p>
              ) : null}
            </div>
          ) : null}
          <div className="grid gap-y-2 js-form-field relative">
            <p className="font-medium">
              Enter your email <sup>*</sup>
            </p>
            <Input
              className="rounded-lg"
              name="email"
              placeholder="Email *"
              type="email"
              validations={['required', 'email']}
            />
          </div>
          <div className="grid gap-y-2 js-form-field relative">
            <Toggle
              className="border-gray-300"
              type="checkbox"
              name="accepted_terms_of_service"
              validations={['required']}
              options={[
                {
                  label: (
                    <p className="text-sm">
                      {translations['I have read and accept the']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/terms-of-use"
                      >
                        {translations['terms of service']}
                      </a>{' '}
                      {translations['and']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/privacy-policy"
                      >
                        {translations['privacy policy']}
                      </a>
                    </p>
                  ),
                  value: '1',
                },
              ]}
            />
          </div>
          <div className="mt-2 w-full">
            <Button
              type="submit"
              label={formSubmitText}
              variant="primary"
              name="commit"
              id="submitt_embeded_step_1"
              className="w-full rounded-lg"
              loading={isLoading}
              data-disable-with={formSubmitText}
            />
          </div>
        </Form>
      </div>
    );
  },
);

export default SignupFormPasswordless;
